import { computed, Injectable, Signal, signal } from '@angular/core';
import {
  AdminChatStatusModel,
  ChatConversationModel,
  ChatMessageModel,
  ChatPersonModel,
} from '../shared/models/chat.model';
import { MentionableText } from '../shared/models/mention.model';
import { ChatHelper } from '../shared/utils/chat-helper';

@Injectable({ providedIn: 'root' })
export class ChatConversationStore {
  private readonly _conversations = signal<ChatConversationModel[]>([]);
  private readonly _messagesByConversationId = signal<ReadonlyMap<Guid, ChatMessageModel[]>>(new Map());
  private readonly _hasOlderByConversationId = signal<ReadonlyMap<Guid, boolean>>(new Map());
  private readonly _draftsByConversationId = signal<ReadonlyMap<Guid, MentionableText>>(new Map());

  private readonly _status = signal<AdminChatStatusModel | null>(null);
  private readonly _peopleByUserId = signal<ReadonlyMap<Guid, ChatPersonModel>>(new Map());
  private readonly _unreadFacilityIds = signal<Guid[]>([]);
  private readonly _loadedFacilityId = signal<Guid | null>(null);
  private readonly _manageableFacilityIds = signal<Guid[]>([]);

  readonly conversations = this._conversations.asReadonly();
  readonly status = this._status.asReadonly();
  readonly peopleByUserId = this._peopleByUserId.asReadonly();
  readonly unreadFacilityIds = this._unreadFacilityIds.asReadonly();
  readonly loadedFacilityId = this._loadedFacilityId.asReadonly();
  readonly manageableFacilityIds = this._manageableFacilityIds.asReadonly();

  private readonly messageSignalCache = new Map<Guid, Signal<ChatMessageModel[]>>();

  messagesFor(conversationId: Guid): Signal<ChatMessageModel[]> {
    let messages = this.messageSignalCache.get(conversationId);

    if (!messages) {
      messages = computed(() => this._messagesByConversationId().get(conversationId) ?? []);
      this.messageSignalCache.set(conversationId, messages);
    }

    return messages;
  }

  hasOlderMessages(conversationId: Guid): boolean {
    const hasOlder = this._hasOlderByConversationId().get(conversationId) ?? false;

    return hasOlder;
  }

  draftFor(conversationId: Guid): MentionableText | undefined {
    return this._draftsByConversationId().get(conversationId);
  }

  setDraft(conversationId: Guid, draft: MentionableText): void {
    this._draftsByConversationId.update((draftsById) => {
      const next = new Map(draftsById);

      if (draft.body.trim()) {
        next.set(conversationId, draft);
      } else {
        next.delete(conversationId);
      }

      return next;
    });
  }

  setStatus(status: AdminChatStatusModel | null): void {
    this._status.set(status);
  }

  setPeople(people: ChatPersonModel[]): void {
    this._peopleByUserId.set(new Map(people.map((person) => [person.userId, person])));
  }

  setUnreadFacilityIds(facilityIds: Guid[]): void {
    this._unreadFacilityIds.set([...facilityIds]);
  }

  setLoadedFacilityId(facilityId: Guid | null): void {
    this._loadedFacilityId.set(facilityId);
  }

  setManageableFacilityIds(facilityIds: Guid[]): void {
    this._manageableFacilityIds.set([...facilityIds]);
  }

  conversationById(conversationId: Guid): ChatConversationModel | undefined {
    const conversation = this._conversations().find((candidate) => candidate.id === conversationId);

    return conversation;
  }

  setConversations(conversations: ChatConversationModel[]): void {
    this._conversations.set(conversations);
  }

  upsertConversation(conversation: ChatConversationModel): void {
    this._conversations.update((conversations) => {
      const remaining = conversations.filter((existing) => existing.id !== conversation.id);

      return [conversation, ...remaining];
    });
  }

  private readonly _scrollToMessageRequest = signal<{ conversationId: Guid; messageId: Guid } | null>(null);

  readonly scrollToMessageRequest = this._scrollToMessageRequest.asReadonly();

  requestScrollToMessage(conversationId: Guid, messageId: Guid): void {
    this._scrollToMessageRequest.set({ conversationId: conversationId, messageId: messageId });
  }

  clearScrollToMessageRequest(): void {
    this._scrollToMessageRequest.set(null);
  }

  removeConversation(conversationId: Guid): void {
    this._conversations.update((conversations) =>
      conversations.filter((conversation) => conversation.id !== conversationId)
    );

    this._messagesByConversationId.update((messagesByConversationId) => {
      const remaining = new Map(messagesByConversationId);

      remaining.delete(conversationId);

      return remaining;
    });

    this._hasOlderByConversationId.update((hasOlderByConversationId) => {
      const remaining = new Map(hasOlderByConversationId);

      remaining.delete(conversationId);

      return remaining;
    });
  }

  patchConversation(conversationId: Guid, patch: Partial<ChatConversationModel>): void {
    this._conversations.update((conversations) =>
      conversations.map((conversation) =>
        conversation.id === conversationId ? { ...conversation, ...patch } : conversation
      )
    );
  }

  patchMemberDateMuted(conversationId: Guid, userId: Guid, dateMuted: string | null): void {
    this._conversations.update((conversations) =>
      conversations.map((conversation) => {
        if (conversation.id !== conversationId) {
          return conversation;
        }

        const members = conversation.members.map((member) =>
          member.userId === userId ? { ...member, dateMuted: dateMuted } : member
        );

        return { ...conversation, members: members };
      })
    );
  }

  patchMemberReadState(conversationId: Guid, userId: Guid, lastReadDate: string | null, dateRead: string | null): void {
    this._conversations.update((conversations) =>
      conversations.map((conversation) => {
        if (conversation.id !== conversationId) {
          return conversation;
        }

        const members = conversation.members.map((member) =>
          member.userId === userId ? { ...member, lastReadDate: lastReadDate, dateRead: dateRead } : member
        );

        return { ...conversation, members: members };
      })
    );
  }

  setMessages(conversationId: Guid, messages: ChatMessageModel[], hasOlder: boolean): void {
    this.updateMessages(conversationId, () => this.sortMessages(messages));
    this.setHasOlder(conversationId, hasOlder);
  }

  prependOlderMessages(conversationId: Guid, olderMessages: ChatMessageModel[], hasOlder: boolean): void {
    this.updateMessages(conversationId, (current) => this.sortMessages([...olderMessages, ...current]));
    this.setHasOlder(conversationId, hasOlder);
  }

  upsertMessage(conversationId: Guid, message: ChatMessageModel): void {
    this.updateMessages(conversationId, (current) => {
      const remaining = current.filter(
        (existing) => existing.clientMessageId !== message.clientMessageId && existing.id !== message.id
      );

      return this.sortMessages([...remaining, message]);
    });
  }

  patchMessageReaction(conversationId: Guid, messageId: Guid, userId: Guid, reaction: string | null): void {
    this.updateMessages(conversationId, (current) =>
      current.map((message) =>
        message.id === messageId
          ? { ...message, reactions: this.reactionsAfterPatch(message, userId, reaction) }
          : message
      )
    );
  }

  removeMessageByClientId(conversationId: Guid, clientMessageId: Guid): void {
    this.updateMessages(conversationId, (current) =>
      current.filter((existing) => existing.clientMessageId !== clientMessageId)
    );
  }

  clear(): void {
    this._conversations.set([]);
    this._messagesByConversationId.set(new Map());
    this._hasOlderByConversationId.set(new Map());
    this._draftsByConversationId.set(new Map());
    this._status.set(null);
    this._peopleByUserId.set(new Map());
    this._unreadFacilityIds.set([]);
    this._loadedFacilityId.set(null);
    this._scrollToMessageRequest.set(null);
    this.messageSignalCache.clear();
  }

  private updateMessages(
    conversationId: Guid,
    mutate: (current: ChatMessageModel[]) => ChatMessageModel[]
  ): void {
    this._messagesByConversationId.update((messagesById) => {
      const next = new Map(messagesById);
      const current = next.get(conversationId) ?? [];

      next.set(conversationId, mutate(current));

      return next;
    });
  }

  private reactionsAfterPatch(
    message: ChatMessageModel,
    userId: Guid,
    reaction: string | null
  ): ChatMessageModel['reactions'] {
    if (reaction === null) {
      return message.reactions.filter((candidate) => candidate.userId !== userId);
    }

    const hasExisting = message.reactions.some((candidate) => candidate.userId === userId);

    if (!hasExisting) {
      return [...message.reactions, { userId: userId, reaction: reaction, reactedDate: new Date().toISOString() }];
    }

    return message.reactions.map((candidate) =>
      candidate.userId === userId ? { ...candidate, reaction: reaction } : candidate
    );
  }

  private setHasOlder(conversationId: Guid, hasOlder: boolean): void {
    this._hasOlderByConversationId.update((hasOlderById) => {
      const next = new Map(hasOlderById);

      next.set(conversationId, hasOlder);

      return next;
    });
  }

  private sortMessages(messages: ChatMessageModel[]): ChatMessageModel[] {
    const stored = messages.filter((message) => !message.isPending);
    const pending = messages.filter((message) => message.isPending);

    stored.sort((a, b) => {
      const dateDiff = new Date(a.sentDate).getTime() - new Date(b.sentDate).getTime();

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return ChatHelper.compareSqlGuids(a.id, b.id);
    });
    pending.sort((a, b) => new Date(a.sentDate).getTime() - new Date(b.sentDate).getTime());

    return [...stored, ...pending];
  }
}
